import React, {useEffect, useMemo, useState} from 'react';
import {Card, Button, Text, Label, Icon} from '@gravity-ui/uikit';
import {Pencil, Plus} from '@gravity-ui/icons';

import {useEditFundraiserForm} from '../../model';
import {FormInput, TextEditor, useErrorHandler, ErrorModal} from '../../../../shared';
import styles from './EditFundraiserForm.module.scss';
import {useNavigate} from 'react-router';
import {SelectRegions, SelectFunds, useCategoryGroupsWithCategories} from '../../../../entities';
import {SelectCategoriesByGroupsModal} from '../../../categories';

export const EditFundraiserForm = () => {
  const navigate = useNavigate();
  const {handleError, isErrorModalOpen, errorInfo, handleClose} = useErrorHandler({
    resourceName: 'Сбор',
    title: 'Ошибка при редактировании сбора',
  });

  const {
    values,
    isValid,
    handleChange,
    setFieldValue,
    handleSubmit,
    handleBlur,
    touched,
    errors,
    isLoading,
    submitError,
  } = useEditFundraiserForm();

  // Обработчики изменения значений полей
  const handleFundChange = (newValue: string[]) => {
    setFieldValue('fundId', newValue[0] || '');
  };

  const handleRegionChange = (newValue: string[]) => {
    setFieldValue('regionId', newValue[0] || '');
  };

  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const {data: categoryGroupsWithCategories} = useCategoryGroupsWithCategories();

  const categoryNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (categoryGroupsWithCategories || []).forEach((group) => {
      group.categories.forEach((category) => {
        map[category.id] = category.name;
      });
    });
    return map;
  }, [categoryGroupsWithCategories]);

  const selectedCategoryNames = useMemo(
    () => values.categoryIds.map((id) => categoryNameById[id] || id),
    [values.categoryIds, categoryNameById],
  );

  const handleCategoriesApply = (ids: string[]) => {
    setFieldValue('categoryIds', ids);
  };

  const handleCategoryClose = (index: number) => {
    const newCategoryIds = [...values.categoryIds];
    newCategoryIds.splice(index, 1);
    setFieldValue('categoryIds', newCategoryIds);
  };

  const handleEditorBlur = (content: string) => {
    const processedContent = content === '<p><br></p>' ? '' : content;
    setFieldValue('detailedDescription', processedContent);
  };

  const handleCancel = () => {
    navigate('/fundraisings/appeals');
  };

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Card className={styles.content}>
          <Text variant="subheader-3">Информация о сборе</Text>

          <FormInput
            label="Название сбора"
            placeholder="Введите название сбора"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            size="m"
            isRequired={true}
            error={touched.name && errors.name}
          />

          <FormInput
            label="Короткое название"
            placeholder="Введите короткое название сбора"
            name="shortName"
            value={values.shortName || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            size="m"
            error={touched.shortName && errors.shortName}
          />
          <Text variant="body-1" color="secondary">
            Если не указано — в сообщениях используется полное название
          </Text>

          <FormInput
            label="Номер сбора"
            placeholder="Введите номер сбора (необязательно)"
            name="externalId"
            value={values.externalId || ''}
            onChange={(e) => {
              setFieldValue('externalId', e.target.value || null);
            }}
            onBlur={handleBlur}
            size="m"
            error={touched.externalId && errors.externalId}
          />

          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Фонд<span className={styles.required}>*</span>
            </Text>
            <SelectFunds
              value={[values.fundId]}
              onUpdate={handleFundChange}
              name="fundId"
              error={touched.fundId && errors.fundId}
            />
          </div>

          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Регион<span className={styles.required}>*</span>
            </Text>
            <SelectRegions
              value={[values.regionId]}
              onUpdate={handleRegionChange}
              name="regionId"
              error={touched.regionId && errors.regionId}
            />
          </div>
          <div className={styles.field}>
            <Text variant="subheader-1" className={styles.label}>
              Категории<span className={styles.required}>*</span>
            </Text>
            <div className={styles.categoriesPicker}>
              {selectedCategoryNames.length > 0 && (
                <div className={styles.categoryChips}>
                  {selectedCategoryNames.map((name, index) => (
                    <Label
                      type="close"
                      theme="normal"
                      onCloseClick={() => handleCategoryClose(index)}
                      key={`${values.categoryIds[index]}-${name}`}>
                      {name}
                    </Label>
                  ))}
                </div>
              )}
              <Button view="normal" size="m" onClick={() => setIsCategoriesModalOpen(true)}>
                <Icon data={values.categoryIds.length > 0 ? Pencil : Plus} />
                {values.categoryIds.length > 0 ? 'Редактировать' : 'Добавить категории'}
              </Button>

              {touched.categoryIds && errors.categoryIds && (
                <Text variant="body-1" color="danger">
                  {errors.categoryIds as string}
                </Text>
              )}
            </div>
          </div>

          <FormInput
            label="Краткое описание"
            placeholder="Введите краткое описание"
            name="shortDescription"
            value={values.shortDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            size="m"
            isRequired={true}
            isTextarea
            error={touched.shortDescription && errors.shortDescription}
          />

          <div className={styles.textEditorContainer}>
            <Text variant="subheader-1" whiteSpace="nowrap">
              Подробное описание<span className={styles.required}>*</span>
            </Text>
            <div className={styles.editorContainer}>
              <TextEditor
                value={values.detailedDescription}
                onBlur={handleEditorBlur}
                name="detailedDescription"
                isError={!!errors.detailedDescription}
              />
              {errors.detailedDescription && (
                <Text variant="body-1" color="danger">
                  {errors.detailedDescription}
                </Text>
              )}
            </div>
          </div>
        </Card>

        <div className={styles.buttons}>
          <Button view="flat-secondary" size="l" onClick={handleCancel} disabled={isLoading}>
            Отменить
          </Button>
          <Button view="action" size="l" type="submit" disabled={!isValid} loading={isLoading}>
            Сохранить
          </Button>
        </div>
      </form>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
      <SelectCategoriesByGroupsModal
        open={isCategoriesModalOpen}
        setOpen={setIsCategoriesModalOpen}
        selectedCategoryIds={values.categoryIds}
        onApply={handleCategoriesApply}
      />
    </>
  );
};
